import React from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Chip,
    Stack,
    Button,
    CircularProgress,
} from '@mui/material';
import { Edit, AutoAwesome, Refresh } from '@mui/icons-material';
import { PromptVersion } from '../types';

// AI suggestion chips for prompt modifications
const AI_SUGGESTIONS = [
    'Increase technical skills weight',
    'Focus more on culture fit',
    'Prioritize recent experience',
    'Add leadership evaluation',
    'Be stricter on experience requirements',
    'Weight communication skills higher',
];

interface RefineTabProps {
    selectedVersion?: PromptVersion;
    displayPrompt: string;
    experimentName: string;
    setExperimentName: (name: string) => void;
    modificationRequest: string;
    setModificationRequest: (request: string) => void;
    isRegenerating: boolean;
    handleRegeneratePrompt: () => void;
    handleSuggestionClick: (suggestion: string) => void;
    onNext: () => void;
    evaluationExperiment?: boolean | null;
}

export const RefineTab: React.FC<RefineTabProps> = ({
    selectedVersion,
    displayPrompt,
    experimentName,
    setExperimentName,
    modificationRequest,
    setModificationRequest,
    isRegenerating,
    handleRegeneratePrompt,
    handleSuggestionClick,
    onNext,
    evaluationExperiment,
}) => {
    const [isDirty, setIsDirty] = React.useState(false);

    // Reset isDirty when a new version is selected or regeneration finishes
    React.useEffect(() => {
        if (!isRegenerating) {
            setIsDirty(false);
        }
    }, [selectedVersion?.id, isRegenerating]);

    const onModificationChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setModificationRequest(e.target.value);
        setIsDirty(true);
    };

    const handleSave = () => {
        handleRegeneratePrompt();
        // isDirty will be reset by the useEffect when isRegenerating goes true -> false
    };

    const isNextDisabled = evaluationExperiment === true ? (isDirty || isRegenerating) : false;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Experiment Name */}
            {!evaluationExperiment && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 1.5,
                        bgcolor: '#f8f9fa',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Edit sx={{ fontSize: 20, color: 'primary.main' }} />
                        <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                            Experiment Name
                        </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                        {selectedVersion?.status === 'DRAFT'
                            ? 'Give your experiment a descriptive name to easily identify it later.'
                            : 'Name your experiment before generating. Leave blank for auto-naming.'
                        }
                    </Typography>

                    <TextField
                        fullWidth
                        size="small"
                        placeholder="e.g., Python Focus Test (optional)"
                        value={experimentName}
                        onChange={(e) => setExperimentName(e.target.value)}
                        sx={{
                            bgcolor: 'white',
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'divider',
                                },
                            },
                        }}
                    />
                </Paper>
            )}

            {/* Prompt Display Section */}
            {!evaluationExperiment && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 1.5,
                        bgcolor: '#f8f9fa',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <AutoAwesome sx={{ fontSize: 20, color: 'primary.main' }} />
                        <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                            {selectedVersion?.status === 'DRAFT'
                                ? 'Experiment Prompt'
                                : selectedVersion?.isActive
                                    ? 'Current Active Prompt'
                                    : 'Selected Prompt'
                            }
                        </Typography>
                        {/* Show tags only for non-draft versions */}
                        {selectedVersion?.status !== 'DRAFT' && (
                            <>
                                <Chip
                                    label={`v${selectedVersion?.version || 1}`}
                                    color="primary"
                                    size="small"
                                    sx={{ height: 18, fontSize: '0.7rem' }}
                                />
                                {selectedVersion?.isActive && (
                                    <Chip
                                        label="Active"
                                        color="success"
                                        size="small"
                                        sx={{ height: 18, fontSize: '0.7rem' }}
                                    />
                                )}
                            </>
                        )}
                    </Box>


                    <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                        {selectedVersion?.status === 'DRAFT' ? (
                            <>
                                Based on version {selectedVersion.version}
                            </>
                        ) : (
                            <>
                                {selectedVersion ? new Date(selectedVersion.createdAt).toLocaleDateString() : 'N/A'}
                            </>
                        )}
                    </Typography>


                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            bgcolor: 'white',
                            overflow: 'auto',
                            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                            fontSize: '0.8rem',
                            lineHeight: 1.6,
                            whiteSpace: 'pre-wrap',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            minHeight: '200px',
                            maxHeight: '300px',
                            color: 'text.primary',
                        }}
                    >
                        {displayPrompt}
                    </Paper>
                </Paper>
            )}

            {/* AI Modification Request */}
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    bgcolor: '#f8f9fa',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <AutoAwesome sx={{ fontSize: 20, color: 'primary.main' }} />
                    <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                        What would you like to change?
                    </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    Describe your changes in plain language. AI will modify the prompt accordingly.
                </Typography>

                {/* Suggestion Chips */}
                {/* Dont show suggestion chips for now */}
                {false && <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
                    {AI_SUGGESTIONS.map((suggestion) => (
                        <Chip
                            key={suggestion}
                            label={suggestion}
                            size="small"
                            onClick={() => handleSuggestionClick(suggestion)}
                            sx={{
                                cursor: 'pointer',
                                bgcolor: 'white',
                                border: '1px solid',
                                borderColor: 'divider',
                                '&:hover': {
                                    bgcolor: 'primary.light',
                                    borderColor: 'primary.main',
                                    color: 'primary.contrastText',
                                },
                            }}
                        />
                    ))}
                </Stack>}

                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    placeholder="E.g., 'Weight Python and AWS skills more heavily' or 'Focus on leadership experience for senior roles'"
                    value={modificationRequest}
                    onChange={onModificationChange}
                    sx={{
                        bgcolor: 'white',
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: 'divider',
                            },
                        },
                    }}
                />

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={isRegenerating ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                        onClick={handleSave}
                        disabled={!modificationRequest.trim() || isRegenerating}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                        }}
                    >
                        {isRegenerating
                            ? (evaluationExperiment === true ? 'Saving...' : 'Refining...')
                            : (evaluationExperiment === true ? 'Save' : 'Regenerate Prompt')}
                    </Button>
                    {(selectedVersion?.status === 'DRAFT' || (evaluationExperiment === true && selectedVersion)) && (
                        <Button
                            variant="outlined"
                            onClick={onNext}
                            disabled={isNextDisabled}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                            }}
                        >
                            Next →
                        </Button>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};
